import React, { useCallback, useMemo, useRef, useState, useLayoutEffect } from "react";
import clsx from "clsx";
import { NavLink } from "react-router-dom";
import { Dialog, DialogContent, DialogActions, Grid } from "@mui/material";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { Stack, Button, Skeleton, Box, Alert } from "@curves/ui-base";
import { useMemberProfileApi } from "@apis/useMemberProfileApi";
import useInBodyApi from "@apis/useInBodyApi";
import { MemberInfoCard } from "@icoach/components/";
import { IN_BODY_INTERVIEW_TABS, MEASURE_REFERENCE_TYPE, MEMBERS_MEMBER_DETAIL_TABS } from "@icoach/constants/";
import { CLUB_INBODY_ROUTER, CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";
import useLanding from "@icoach/landingPage/components/useLanding";
import { AgreedNotesBox, GuidanceNotesBox, HealthDeclarationBox, SpecialBodyConditionBox, TodoCard, WorkoutMetrics, XFactorNotesBox } from "@icoach/memberDetail/components";
import MemberSummaryTipDialogBox from "@icoach/landingPage/dialog/MemberSummaryTipDialogBox";
import { isNumber } from "@util/utils";

const ViewLoadingMemberTipBox = () => {
	return (
		<Grid container spacing={3}>
			<Grid item xs={5}>
				<Stack spacing={2} alignItems="center">
					<Skeleton variant="circular" width={80} height={80} />
					<Stack direction="column" spacing={1} style={{ flexGrow: 1 }}>
						<Skeleton variant="text" height={40} />
						<Skeleton variant="text" height={40} />
					</Stack>
				</Stack>
			</Grid>
			<Grid item xs={7}>
				<Stack spacing={2} justifyContent="end">
					<Skeleton variant="rectangular" width={90} height={90} />
					<Skeleton variant="rectangular" width={90} height={90} />
					<Skeleton variant="rectangular" width={90} height={90} />
				</Stack>
			</Grid>
			<Grid item xs={6}>
				<Stack direction="column" spacing={2}>
					<Skeleton variant="rectangular" height={100} />
					<Skeleton variant="rectangular" height={100} />
					<Skeleton variant="rectangular" height={100} />
				</Stack>
			</Grid>
			<Grid item xs={6}>
				<Stack direction="column" spacing={2}>
					<Skeleton variant="rectangular" height={80} />
					<Skeleton variant="rectangular" height={80} />
				</Stack>
			</Grid>
		</Grid>
	);
};

const MemberSummaryTipDialog = React.forwardRef((props, ref) => {
	const { onClose: onCloseProps, open, sourceData: sourceDataProps = {}} = props;
	if (!open) return null;
	const { memberID } = sourceDataProps || {};
	const { dialogRef } = useLanding();
	const { getMemberProfileTipApi } = useMemberProfileApi();
	const { getMemberHeightApi } = useInBodyApi();
	const [sourceData, setSourceData] = useState(null);
	const smartBandUnpaid = sourceData?.isOverdrawDeviceOrder; // 是否已有智能欠費暫時開關

	const todoRef = useRef(null);
	const memberSummaryTipDialogRef = useRef(null);

	const handleCloseClick = e => onCloseProps && onCloseProps(null);

	const getHeightApi = useCallback(
		async () => {
			const resp = await getMemberHeightApi(memberID);
			if (isNumber(resp)) {
				const params = {
					peopleID: memberID,
					referenceType: MEASURE_REFERENCE_TYPE.MEMBER,
					height: resp,
					nickName: sourceData.nickName,
					isMenHidden: true,
				};
				dialogRef.current.openInBodyStartScanDialogs(params);
			}
		},
		// eslint-disable-next-line
		[sourceData]
	);

	const getMemberInfoTipApi = async () => {
		const resp = await getMemberProfileTipApi(memberID);
		if (resp) setSourceData(resp);
	};

	const actionData = useMemo(
		() => {
			return [
				{
					label: "查詢智能欠費",
					variant: "outlined",
					onClick: () => {
						memberSummaryTipDialogRef.current.openSmartFitnessBandOverdueDialog({
							memberID,
							memberName: sourceData.memberName,
							memberNo: sourceData.memberNo,
							isOverdrawDeviceOrder: sourceData.isOverdrawDeviceOrder,
							deviceOrderOverdrawText: sourceData.deviceOrderOverdrawText,
							dialogRef: memberSummaryTipDialogRef,
						}, onCloseProps);
					},
					isShow: smartBandUnpaid,
				},
				{
					label: "查詢欠費明細",
					variant: "outlined",
					onClick: () => {
						memberSummaryTipDialogRef.current &&
							memberSummaryTipDialogRef.current.openCheckoutCreditOrderDialog({
								memberID,
							});
					},
					to: null,
					isShow: true,
				},
				{
					label: "編輯會員資訊",
					variant: "outlined",
					component: NavLink,
					to: CLUB_MEMBERS_ROUTER.MEMBER_DETAIL.GET_TABS_PATH(memberID, MEMBERS_MEMBER_DETAIL_TABS.WORKOUT),
					isShow: true,
				},
				{
					label: "開始面談",
					variant: "outlined",
					component: NavLink,
					to: CLUB_INBODY_ROUTER.GET_INTERVIEW_PATH(IN_BODY_INTERVIEW_TABS.SUMMARY, memberID),
					isShow: true,
				},
				{
					label: "立刻量身",
					variant: "contained",
					onClick: () => getHeightApi(),
					to: null,
					isShow: true,
				},
			];
		},
		// eslint-disable-next-line
		[getHeightApi]
	);

	useLayoutEffect(
		() => {
			open ? getMemberInfoTipApi() : setSourceData(null);
		},
		// eslint-disable-next-line
		[open]
	);

	return (
		<Dialog className="member-summary-dialog" PaperProps={{ className: "wx-80rem" }} open={open} fullWidth>
			<GradientColorHeader onClose={handleCloseClick}>會員狀態</GradientColorHeader>
			{sourceData ? (
				<React.Fragment>
					<DialogContent>
						<Grid container spacing={2}>
							<Grid xs={4} item>
								<MemberInfoCard
									name={sourceData.memberName}
									nickname={sourceData.nickName}
									avatar={sourceData.picturePath}
									avatarWidth={7.5}
									memberNo={sourceData.memberNo}
									membershipStatus={sourceData.membershipStatus || 0}
								/>
							</Grid>
							<Grid xs={8} item>
								<WorkoutMetrics
									weeklyWorkoutQuantity={sourceData.weeklyWorkoutQuantity}
									monthlyWorkoutQuantity={sourceData.monthlyWorkoutQuantity}
									totalWorkoutQuantity={sourceData.totalWorkoutQuantity}
								/>
							</Grid>
						</Grid>
						{smartBandUnpaid && (
							<Grid container className="pt-4" spacing={2}>
								<Grid item xs={12}>
									<Alert severity="error" variant="filled" color="error">
										{sourceData?.deviceOrderOverdrawText}
									</Alert>
								</Grid>
							</Grid>
						)}
						<Grid container className="pt-4" spacing={2}>
							<Grid item xs={6}>
								<XFactorNotesBox xFactorNotes={sourceData.xFactorNotes} className="mb-2" />
								<AgreedNotesBox agreedNotes={sourceData.agreedNotes} className="mb-2" />
								<GuidanceNotesBox guidanceNotes={sourceData?.guidanceNotes} className="mb-2" />
								<SpecialBodyConditionBox specialBodyCondition={sourceData?.specialBodyCondition} className="mb-2" />
								<HealthDeclarationBox memberID={memberID} memberShipStatus={sourceData?.memberShipStatus} healthDeclaration={sourceData?.healthDeclaration} />
							</Grid>
							<Grid item xs={6}>
								<TodoCard
									todoRef={todoRef}
									memberID={memberID}
									todos={sourceData.serviceTasks}
									options={sourceData?.resource?.serviceTodoTextOptions ?? []}
									onClick={() => {
										memberSummaryTipDialogRef.current &&
											memberSummaryTipDialogRef.current.openTodoDialog(
												{
													todos: (todoRef.current && todoRef.current.getTodos()) || sourceData.serviceTasks,
													memberID,
													options: sourceData?.resource?.serviceTodoTextOptions ?? [],
												},
												todoRef.current.onUpdatedTodos
											);
									}}
								/>
							</Grid>
						</Grid>
					</DialogContent>
					<DialogActions className={clsx("member-summary-dialog-actions", "p-3")}>
						<Box className="btn-group">
							{actionData.map((action, index) => {
								const { label, variant, onClick, to, component: Component, isShow } = action;
								return isShow ? (
									<Button key={index} variant={variant} onClick={onClick} component={Component} to={to}>
										{label}
									</Button>
								) : null;
							})}
						</Box>
					</DialogActions>
					{/* 為維持 MemberSummaryTipDialog 為開啟狀態，查詢欠費明細 and 新增待辦事項視窗管理在 MemberSummaryTipDialogBox 裡面 */}
					<MemberSummaryTipDialogBox ref={memberSummaryTipDialogRef} />
				</React.Fragment>
			) : (
				<DialogContent className="mt-3">
					<ViewLoadingMemberTipBox />
				</DialogContent>
			)}
		</Dialog>
	);
});

export default MemberSummaryTipDialog;
